import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { AccordionItem, Spinner } from "@heroui/react";
import { LimeAccordion } from "@/Components/NextBase/LimeAccordion";
import { useClientFeature } from "@/hooks/useClientFeature";
import { api } from "@/lib/api-client";
import useUserPermissionsStore from "@/stores/useUserPermissionsStore";
import { WaitlistStatusChip } from "@/pages/Dashboard/Waitlist/components/WaitlistStatusChip";
import { WaitlistRowActions } from "@/pages/Dashboard/Waitlist/WaitlistRowActions";
import { Timer } from "lucide-react";

export function WaitlistEntries({
  appointmentId,
  openCustomerCard,
  onBookSuccess,
}: {
  appointmentId: number;
  openCustomerCard: (customerId: number) => void;
  onBookSuccess?: () => void;
}) {
  // Defence in depth: the appointment modal mounts this unconditionally,
  // so without the feature gate every unflagged tenant fires
  // `/waitlist/list` on every modal open and gets a 403 — surfaced as a
  // red toast + 3 React Query retries + Sentry noise. Gate at the
  // component level so any future caller picks up the protection for
  // free, and pass `enabled: false` to the query so the request never
  // leaves the browser.
  const { isFeatureEnabled } = useClientFeature();
  const permissions = useUserPermissionsStore((state) => state.permissions);
  const canViewWaitlist =
    isFeatureEnabled("waitlist") && Boolean(permissions.view_waitlist);

  const { data, isLoading } = api.waitlist.useGetList(
    {
      groupAppointmentId: appointmentId,
      page: 1,
      // Use the server's max page size so we cover realistic
      // `maxGroupWaitlistSize` settings without paginating.
      perPage: 100,
      sortBy: "dateJoinedAsc",
      statuses: ["waiting", "notified"],
    },
    { enabled: canViewWaitlist },
  );

  if (!canViewWaitlist) return null;

  const entries = data?.entries ?? [];
  const totalItems = data?.pagination.totalItems ?? entries.length;

  if (!isLoading && totalItems === 0) {
    return null;
  }

  return (
    <div className="mt-2">
      <LimeAccordion
        variant="bordered"
        itemClasses={{
          trigger: "cursor-pointer",
        }}
      >
        <AccordionItem
          textValue={t`Čakalna vrsta`}
          startContent={<Timer size={16} />}
          title={
            <div className="flex items-center gap-2">
              <p className="text-xs font-medium">
                <Trans>Čakalna vrsta</Trans>
              </p>
              <span className="text-default-500 text-xs">({totalItems})</span>
            </div>
          }
        >
          {isLoading && entries.length === 0 ? (
            <div className="flex justify-center py-2">
              <Spinner size="sm" />
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {entries.map((entry) => (
                <div
                  key={entry.waitlistId}
                  className="flex items-center justify-between rounded-lg p-2"
                  data-identifier="waitlist-entry-row"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm">
                      {entry.customer.name} {entry.customer.lastName}
                    </p>
                    <WaitlistStatusChip status={entry.status} />
                  </div>
                  <div className="flex justify-end">
                    <WaitlistRowActions
                      entry={entry}
                      openCustomerCard={openCustomerCard}
                      onBookSuccess={onBookSuccess}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </AccordionItem>
      </LimeAccordion>
    </div>
  );
}
